/**
 * File Preview Component (JavaScript)
 * Reusable element generator matching React-style parameters for logos, attachments, and additional files.
 * 
 * Usage:
 *   const el = window.renderFilePreview({
 *     url: '/files/example.png',
 *     name: 'example.png',
 *     onDelete: 'removeExistingLogo', // string name or function callback
 *     deletable: true,
 *     key: 'logo', // or 'file'
 *     displayType: 'thumb', // or 'list'
 *     pendingId: 'pending-123'
 *   });
 *   container.appendChild(el);
 */

function _formatFileSize(bytes) {
    if (!bytes || isNaN(bytes)) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
}

function renderFilePreview(opts = {}) {
    const {
        url,
        name,
        onDelete,
        deletable = true,
        key = 'file',
        fileId,
        pendingId,
        displayType = 'thumb',
        size,
        status = 'Ready',
        showProgress = false,
        previewable = true
    } = opts;

    if (!url) return null;

    const fileName = name || (url.split('?')[0].split('/').pop() || 'Attachment');
    const clean = fileName.split('?')[0].toLowerCase();
    const isImage = /\.(svg|png|jpe?g|gif|webp)$/i.test(clean) || url.startsWith('data:image/');
    const isLink = /^https?:\/\//i.test(url) && !url.startsWith('blob:') && !isImage;
    const isLocal = url.startsWith('blob:') || url.startsWith('data:');
    const openUrl = window._getDesignFormFileOpenUrl ? window._getDesignFormFileOpenUrl(url) : url;
    const proxiedUrl = isLocal ? url : `/api/method/klaviyo_integration.utils.serve_thumbnail?file_url=${encodeURIComponent(openUrl)}`;
    const isAddl = key === 'file' || key === 'additional_files';

    const el = document.createElement('div');
    el.className = `existing-logo-item ${isAddl ? 'existing-additional-file-item ' : ''}preview-attach-item deletable-preview ${displayType === 'list' ? 'ds-preview-item-list ' : ''}${isImage ? 'image-preview' : 'file-preview'}`;
    el.dataset[`${key}Url`] = url;
    el.dataset.fileUrl = url;
    el.dataset.url = url;
    if (key === 'logo') el.dataset.logoUrl = url;
    if (fileId) el.dataset.fileId = fileId;
    if (pendingId) el.dataset.pendingId = pendingId;
    el.style.position = 'relative';

    const safeName = typeof frappe !== 'undefined' && frappe.utils && frappe.utils.escape_html
        ? frappe.utils.escape_html(fileName)
        : fileName.replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
    const safeStatus = typeof frappe !== 'undefined' && frappe.utils && frappe.utils.escape_html
        ? frappe.utils.escape_html(status)
        : status.replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));

    let body = '';

    if (displayType === 'list') {
        const linkUrl = isImage && url && (url.startsWith('/files/') || url.startsWith('/private/files/')) ? proxiedUrl : (openUrl || '#');
        const formattedSize = _formatFileSize(size);
        body = `
            <div class="ds-preview-file">
                <div class="ds-preview-meta">
                    <span class="ds-preview-name"><a href="${linkUrl}" target="_blank" style="color:inherit; text-decoration:underline;">${safeName}</a></span>
                    <span class="ds-preview-subline">${formattedSize ? formattedSize + ' ' : ''}<span data-file-state>${safeStatus}</span></span>
                </div>
            </div>
        `;
    } else if (isImage) {
        const clickAttr = previewable
            ? `onclick="if(window._openPreviewImageModal){event.preventDefault();event.stopPropagation();window._openPreviewImageModal('${proxiedUrl}','${safeName}');}else if(window.DesignSidebarApi&&window.DesignSidebarApi.openPreviewImageModal){event.preventDefault();event.stopPropagation();window.DesignSidebarApi.openPreviewImageModal('${proxiedUrl}','${safeName}');}"`
            : '';
        body = `
            <img src="${proxiedUrl}" alt="${safeName}" class="ds-preview-clickable-image" role="button" tabindex="0" ${clickAttr}>
            <div class="image-preview-name" title="${safeName}">${safeName}</div>
        `;
    } else if (isLink && (url === fileName || !fileName.includes('.'))) {
        body = `
            <div class="ds-preview-file">
                <a href="${url}" target="_blank" title="${url}" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;text-decoration:none;color:inherit;flex-direction:column;">
                    <i class="fa fa-link fa-2x text-muted"></i>
                    <div style="font-size:14px;margin-top:4px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:100%;padding:0 4px;">${safeName}</div>
                </a>
            </div>
        `;
    } else {
        const ext = fileName.includes('.') ? fileName.split('.').pop() : 'FILE';
        body = `
            <div class="ds-preview-file">
                <a href="${openUrl}" target="_blank" title="${safeName}" style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;text-decoration:none;color:inherit;flex-direction:column;">
                    <span class="file-preview-ext">${ext.substring(0, 4).toUpperCase()}</span>
                    <div class="image-preview-name" title="${safeName}">${safeName}</div>
                </a>
            </div>
        `;
    }

    let deleteBtn = '';
    if (deletable) {
        const isFn = typeof onDelete === 'function';
        const clickAttr = !isFn && onDelete ? `onclick="${onDelete}(this)"` : '';
        deleteBtn = `<button type="button" class="logo-delete-btn delete-btn" ${clickAttr} title="Remove file">&times;</button>`;
    }

    let progressHtml = '';
    if (showProgress) {
        progressHtml = `
            <div class="ds-preview-progress" style="position:absolute;bottom:0;left:0;right:0;height:4px;background:#e5e7eb;display:none;border-bottom-left-radius:inherit;border-bottom-right-radius:inherit;overflow:hidden;z-index:2;">
                <div class="ds-preview-progress-fill" style="height:100%;width:0%;background:#ec008c;transition:width 0.1s linear;"></div>
            </div>
        `;
    }

    el.innerHTML = body + deleteBtn + progressHtml;

    if (previewable && isImage && displayType !== 'list') {
        const img = el.querySelector('img.ds-preview-clickable-image');
        if (img) {
            img.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                if (typeof window._openPreviewImageModal === 'function') {
                    window._openPreviewImageModal(proxiedUrl, fileName);
                } else if (window.DesignSidebarApi && window.DesignSidebarApi.openPreviewImageModal) {
                    window.DesignSidebarApi.openPreviewImageModal(proxiedUrl, fileName);
                }
            });
        }
    }

    if (deletable && typeof onDelete === 'function') {
        const btn = el.querySelector('.delete-btn');
        if (btn) {
            btn.addEventListener('click', (e) => onDelete(e, el, opts));
        }
    }

    return el;
}

window.renderFilePreview = renderFilePreview;
window.FilePreview = {
    create: renderFilePreview,
};
